import React, { ReactElement, useLayoutEffect, useMemo, useRef, useState } from 'react';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { BarChart3, TriangleAlert } from 'lucide-react';
import { Card } from '@app/components/core';
import { Skeleton } from '@ui/components/ui/skeleton';
import { hoursExact } from './portfolioHours';
import { ProductHours, hoursCompact, roundUpAxis } from './productTime';

// Bars keep a readable width, so the plot scrolls inside the card rather than the page.
const MIN_BAR_WIDTH = 30; // px
// Bar height (% of the axis) past which the tooltip no longer fits above the bar and flips inside it.
const TOOLTIP_FLIP_AT = 70;
// Closest the tooltip comes to the scroller's edge, which would otherwise clip it.
const TOOLTIP_EDGE_GAP = 4; // px

// Hours per product, tallest first. The average is what makes the long tail legible.
export const HoursPerProductCard = ({
  products,
  isLoading,
  isError,
  periodLabel,
}: {
  products: ProductHours[];
  isLoading: boolean;
  /** Whether the read behind the bars failed. Takes precedence over an empty plot. */
  isError: boolean;
  periodLabel: string;
}): ReactElement => {
  const { t } = useTranslation();
  const [hovered, setHovered] = useState<string | null>(null);
  const [tooltipShift, setTooltipShift] = useState(0);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  // Slid sideways just enough to stay inside the scroller, measured from where it would sit centred.
  useLayoutEffect(() => {
    const tooltip = tooltipRef.current?.getBoundingClientRect();
    const scroller = scrollerRef.current?.getBoundingClientRect();
    if (!tooltip || !scroller) return;
    const left = tooltip.left - tooltipShift;
    const right = tooltip.right - tooltipShift;
    const tooFarLeft = Math.max(0, scroller.left + TOOLTIP_EDGE_GAP - left);
    const tooFarRight = Math.max(0, right - (scroller.right - TOOLTIP_EDGE_GAP));
    setTooltipShift(tooFarLeft - tooFarRight);
  }, [hovered, tooltipShift]);

  const bars = useMemo(
    () => products.filter(p => p.hours > 0).sort((a, b) => b.hours - a.hours || a.name.localeCompare(b.name)),
    [products],
  );
  const average = bars.length ? bars.reduce((sum, p) => sum + p.hours, 0) / bars.length : 0;
  const axisMax = roundUpAxis(Math.max(1, ...bars.map(p => p.hours)));
  const ticks = [...new Set([axisMax, Math.round(axisMax / 2), 0])];
  const y = (value: number): number => (1 - value / axisMax) * 100;

  return (
    <Card className="flex h-full flex-col">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="flex items-center gap-2 text-base font-semibold text-foreground">
          <BarChart3 className="size-4 text-muted-foreground" />
          {t('hoursPerProduct')}
        </p>
        {!isLoading && !isError && bars.length > 0 && (
          <span className="flex items-center gap-2 text-xs text-muted-foreground">
            <span aria-hidden className="h-px w-5 border-t border-dashed border-foreground/70" />
            {t('averageNHours', { hours: hoursExact(average) })}
          </span>
        )}
      </div>

      {isLoading && <Skeleton className="h-64 w-full rounded-xl" />}

      {/* Never a clean-looking empty plot on a failed read: no card on this page is allowed to report a
          result it does not have. */}
      {!isLoading && isError && (
        <p className="flex flex-1 flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-destructive/40 px-4 py-6 text-center text-sm text-muted-foreground">
          <TriangleAlert className="size-5 text-destructive/70" aria-hidden />
          {t('cardReadFailed')}
        </p>
      )}

      {!isLoading && !isError && bars.length === 0 && (
        <p className="flex flex-1 items-center justify-center rounded-xl border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground">
          {t('nothingLoggedInPeriod')}
        </p>
      )}

      {!isLoading && !isError && bars.length > 0 && (
        <div className="flex min-w-0 flex-1">
          {/* Y-axis sits outside the scroller so the hour ticks stay put while the bars pan. */}
          <div className="relative mr-1.5 h-52 w-9 shrink-0">
            {ticks
              .filter(tick => tick !== 0)
              .map(tick => (
                <span
                  key={tick}
                  className="absolute right-0 -translate-y-1/2 text-[10px] leading-none text-muted-foreground tabular-nums"
                  style={{ top: `${y(tick)}%` }}
                >
                  {hoursCompact(tick)}
                </span>
              ))}
          </div>

          <div ref={scrollerRef} className="min-w-0 flex-1 overflow-x-auto pb-1">
            <div style={{ minWidth: `${bars.length * MIN_BAR_WIDTH}px` }}>
              <div className="relative h-52 border-b border-l border-border">
                {ticks.map(tick => (
                  <div
                    key={tick}
                    className="pointer-events-none absolute inset-x-0 border-t border-border/60"
                    style={{ top: `${y(tick)}%` }}
                  />
                ))}

                {/* The average, dashed across the plot. */}
                <div
                  aria-hidden
                  className="pointer-events-none absolute inset-x-0 z-10 border-t border-dashed border-foreground/70"
                  style={{ top: `${y(average)}%` }}
                />

                <div className="flex h-full items-end gap-px px-px">
                  {bars.map(p => {
                    const barPercent = (p.hours / axisMax) * 100;
                    // The scroller clips anything outside the plot, so a tall bar holds its tooltip inside.
                    const sitsAbove = barPercent <= TOOLTIP_FLIP_AT;
                    return (
                      <div
                        key={p.id}
                        onMouseEnter={() => setHovered(p.id)}
                        onMouseLeave={() => setHovered(null)}
                        className="relative flex h-full flex-1 items-end justify-center"
                      >
                        {/* Capped, or a portfolio of four products renders as four slabs. */}
                        <div
                          className={cx(
                            'w-full max-w-12 rounded-t-[2px] bg-primary transition-opacity duration-150',
                            hovered && hovered !== p.id && 'opacity-40',
                          )}
                          style={{ height: `${barPercent}%` }}
                        />
                        {hovered === p.id && (
                          <div
                            ref={tooltipRef}
                            className="pointer-events-none absolute left-1/2 z-20 whitespace-nowrap rounded-md border border-border bg-popover px-2.5 py-1.5 text-xs text-popover-foreground shadow-md"
                            style={{
                              transform: `translateX(calc(-50% + ${tooltipShift}px))`,
                              ...(sitsAbove
                                ? { bottom: `calc(${barPercent}% + 0.375rem)` }
                                : { top: `calc(${100 - barPercent}% + 0.375rem)` }),
                            }}
                          >
                            <p className="font-medium">{p.name}</p>
                            <p className="tabular-nums opacity-80">{t('nHours', { hours: hoursExact(p.hours) })}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Upright names, each ending under its own bar; an angled one would stick out past the scroller and be clipped. */}
              <div className="flex h-20 gap-px px-px">
                {bars.map(p => (
                  <div key={p.id} className="relative flex-1">
                    <span
                      className={cx(
                        'absolute right-1/2 top-1.5 max-w-[4.5rem] truncate text-[10px] leading-none text-muted-foreground transition-opacity duration-150',
                        hovered && hovered !== p.id && 'opacity-40',
                      )}
                      style={{ transform: 'rotate(-90deg) translateY(-50%)', transformOrigin: 'top right' }}
                    >
                      {p.name}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <p className="mt-2 text-xs text-muted-foreground">{t('hoursPerProductCaption', { period: periodLabel })}</p>
    </Card>
  );
};
